"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Play } from "lucide-react";
function extractUrlFromHtml(html: string): string | null {
  if (!html) return null;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || '';
  const urlPattern = /(https?:\/\/[^\s<]+)/g;
  const matches = text.match(urlPattern);
  return matches && matches.length > 0 ? matches[0] : null;
}
function RecordingsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-80 rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-slate-400 mb-4">Failed to load retreat recordings.</p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-center text-slate-400 py-12">
        No retreat recordings available at this time.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const extractedUrl = post.excerpt ? extractUrlFromHtml(post.excerpt) : null;
      const buttonLink = extractedUrl || post.link;
      return <Card key={post.id} data-index={i} className="overflow-hidden border border-border shadow-soft hover:shadow-medium transition-all duration-[150ms] hover:-translate-y-1">
            {post.featuredImage && <div className="relative aspect-video w-full overflow-hidden bg-muted">
                <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm opacity-0 hover:opacity-100 transition-opacity duration-[150ms] flex items-center justify-center">
                  <Play className="size-12 text-slate-900" />
                </div>
              </div>}
            <CardHeader>
              <CardTitle className="text-lg leading-tight">
                {post.title}
              </CardTitle>
              {post.date && <CardDescription className="text-xs">
                  {new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric"
            })}
                </CardDescription>}
            </CardHeader>
            {post.excerpt && <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />
              </CardContent>}
            <CardFooter>
              {buttonLink && <Link to={buttonLink} className="w-full" newTab={!!extractedUrl}>
                  <Button variant="outline" className="w-full">
                    Listen Now
                  </Button>
                </Link>}
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function FeaturedRetreatRecordings() {
  return <section className="py-16 px-6 md:px-12 lg:px-16 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-semibold text-foreground mb-4">
            Featured Retreat Recordings
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore our collection of dharma teachings from recent retreats, offered freely in the spirit of generosity.
          </p>
        </div>
        <PostsProvider className="recordings-grid-wrapper" wp_query={{
        per_page: 6,
        order: "desc",
        orderby: "date"
      }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia"]}>
          <RecordingsConsumer />
        </PostsProvider>
      </div>
    </section>;
}